import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageWrapper } from '../../components/layout';
import { OrgSubnav } from '../../components/shell/OrgSubnav';
import { CompanyViews, CompaniesTable, CompanyPeek } from '../../components/organizations-ledger';
import { CompanyFormDialog } from '../../components/organizations';
import { LedgerDialog, LedgerSheet } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import { CompanyListParams, useCompaniesPage, useCompanyDetail, useDeleteCompany } from '../../hooks/useOrgQueries';
import { useLedgerListKeys } from '../../hooks/useLedgerListKeys';
import { Company } from '../../types/organization.types';
import { cn } from '../../lib/cn';

export const CompaniesPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [peekOpen, setPeekOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState<Company | null>(null);
  const toast = useToast();

  const params: CompanyListParams = useMemo(() => ({ search, page }), [search, page]);
  const { data, isPending, isError } = useCompaniesPage(params);
  const rows = useMemo(() => data?.results ?? [], [data]);
  const count = data?.count ?? 0;

  useEffect(() => {
    if (isError) toast.error('Failed to load companies');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // Tailwind's lg breakpoint; the peek is a right rail on desktop and a bottom
  // sheet on mobile, and the sheet portals to <body>, so CSS can't gate it.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const selectedCompany = rows.find((company) => company.id === selectedId) ?? null;
  // A selected company can leave the current page (filter or page change); fetch
  // it by id so the peek survives.
  const peekFallback = useCompanyDetail(peekOpen && selectedCompany == null ? selectedId : null);
  const peekCompany = selectedCompany ?? peekFallback.data ?? null;

  const move = useCallback(
    (delta: number) => {
      if (rows.length === 0) return;
      const index = rows.findIndex((company) => company.id === selectedId);
      const from = index < 0 ? -delta : index;
      const next = Math.min(rows.length - 1, Math.max(0, from + delta));
      setSelectedId(rows[next].id);
    },
    [rows, selectedId],
  );
  useLedgerListKeys({
    enabled: !formOpen && deleting == null,
    onDown: () => move(1),
    onUp: () => move(-1),
    onPeek: () => setPeekOpen(selectedId != null),
    onClose: () => setPeekOpen(false),
  });

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };
  const resetView = () => {
    setSearch('');
    setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = () => {
    if (!peekCompany) return;
    setEditing(peekCompany);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  // Runs before the dialog's own close path, so `editing` still tells create
  // and update apart here.
  const handleFormSuccess = () => {
    toast.success(editing ? 'Company updated' : 'Company created');
  };

  const deleteCompany = useDeleteCompany();
  const confirmDelete = () => {
    if (!deleting) return;
    const target = deleting;
    // Close synchronously so a second click can't queue a duplicate delete.
    setDeleting(null);
    deleteCompany.mutate(target.id, {
      onSuccess: () => {
        // Companies are server-paged and an out-of-range page 404s. The hook's
        // invalidation has already started a doomed refetch of this page by
        // the time this runs; stepping back switches the observer to the valid
        // page key in the same render batch, so that 404 lands on an
        // unobserved cache entry instead of the screen.
        if (rows.length === 1 && page > 1) setPage(page - 1);
        toast.success('Company deleted');
        setPeekOpen(false);
        setSelectedId(null);
      },
      onError: () => {
        toast.error('Delete failed');
      },
    });
  };

  const cancelRef = useRef<HTMLButtonElement>(null);
  const deleteConfirm = (
    <LedgerDialog
      open={deleting != null}
      onOpenChange={(next) => {
        if (!next) setDeleting(null);
      }}
      title="Delete company?"
      description={
        deleting ? `This permanently removes ${deleting.name}, including all of its teams and departments.` : undefined
      }
      size="sm"
      preventClose={deleteCompany.isPending}
      initialFocus={cancelRef}
      footer={
        <>
          <button
            type="button"
            ref={cancelRef}
            onClick={() => setDeleting(null)}
            disabled={deleteCompany.isPending}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleteCompany.isPending}
            className="cursor-pointer bg-sev-critical px-3 py-1.5 text-scale-1 font-semibold text-paper hover:opacity-90 disabled:cursor-default disabled:opacity-60"
          >
            Delete
          </button>
        </>
      }
    >
      {null}
    </LedgerDialog>
  );

  return (
    <PageWrapper
      title="Companies"
      subtitle={isPending ? undefined : `${count} ${count === 1 ? 'company' : 'companies'}`}
      noPaper
      maxWidth="xl"
      actions={
        <button
          type="button"
          onClick={openCreate}
          className="cursor-pointer bg-accent px-3 py-1 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
        >
          + Add company
        </button>
      }
    >
      <OrgSubnav />

      <div className="hidden md:block">
        <CompanyViews search={search} onSearchChange={changeSearch} />
      </div>
      <div className="flex justify-end md:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          className="cap-label cursor-pointer"
        >
          Filter <span aria-hidden>▾</span>
        </button>
      </div>
      <LedgerSheet open={sheetOpen} onOpenChange={setSheetOpen} title="Views & search">
        <CompanyViews search={search} onSearchChange={changeSearch} />
      </LedgerSheet>

      <div className={cn('mt-1', peekOpen && peekCompany && isDesktop && 'lg:grid lg:grid-cols-[1fr_340px]')}>
        <div className="min-w-0">
          <CompaniesTable
            rows={rows}
            count={count}
            page={page}
            isPending={isPending}
            hasNext={data?.next != null}
            hasPrevious={data?.previous != null}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id);
              setPeekOpen(true);
            }}
            onPageChange={setPage}
            onResetView={resetView}
          />
        </div>
        {peekOpen && peekCompany && isDesktop && (
          <CompanyPeek company={peekCompany} onClose={() => setPeekOpen(false)} onEdit={openEdit} onDelete={setDeleting} />
        )}
      </div>

      <LedgerSheet
        open={peekOpen && !isDesktop && peekCompany != null}
        onOpenChange={(open) => {
          if (!open) setPeekOpen(false);
        }}
        title="Company"
      >
        {peekCompany && (
          <>
            <CompanyPeek company={peekCompany} onClose={() => setPeekOpen(false)} onEdit={openEdit} onDelete={setDeleting} />
            {/* Nested inside the sheet on purpose: sibling Base UI roots would
                both receive a single Escape and close together. */}
            {!isDesktop && deleteConfirm}
          </>
        )}
      </LedgerSheet>
      {isDesktop && deleteConfirm}

      <CompanyFormDialog open={formOpen} onClose={closeForm} onSuccess={handleFormSuccess} company={editing} />
    </PageWrapper>
  );
};
